import api from '@/libs/api'
import store from '@/store'

const getProjectId = () => store.state.app.projectId

export default {
  namespaced: true,
  state: {
    serviceUnavailable: false,
    serviceRequested: false,
    instances: [],
    resultInfo: {},
    currentPage: 1,
    fetchingInstances: false,
    instance: {},
    gettingInstance: false,
    addingInstance: false,
    updatingInstance: false,
    removingInstance: false,
    enablingFirewall: false,
    settingFirewall: false,
    settingInstanceStatus: false,
    // DATACENTERS
    datacenters: [],
    fetchingDatacenters: false,
    // PLANS
    plans: [],
    fetchingPlans: false,
    // IMAGES
    images: [],
    fetchingImages: false,
    // CONSOLE
    console: {},
    gettingConsole: false,
  },
  getters: {
    currentPage: state => state.currentPage,
  },
  mutations: {
    SET_SERVICE_UNAVAILABLE(state, value) {
      state.serviceUnavailable = true
      state.serviceRequested = value
    },
    SET_INSTANCES(state, value) {
      state.instances = value.result
      state.resultInfo = value.resultInfo
    },
    SET_FETCHING_INSTANCES(state, value) {
      state.fetchingInstances = value
    },
    SET_INSTANCE(state, value) {
      state.instance = value.result
    },
    SET_GETTING_INSTANCE(state, value) {
      state.gettingInstance = value
    },
    SET_ADDING_INSTANCE(state, value) {
      state.addingInstance = value
    },
    SET_UPDATING_INSTANCE(state, value) {
      state.updatingInstance = value
    },
    SET_REMOVING_INSTANCE(state, value) {
      state.removingInstance = value
    },
    REMOVE_INSTANCE(state, id) {
      state.instances = state.instances.filter(el => el.id !== id)
    },
    SET_ENABLING_FIREWALL(state, value) {
      state.enablingFirewall = value
    },
    SETTING_FIREWALL(state, value) {
      state.settingFirewall = value
    },
    SET_INSTANCE_PLAN(state, value) {
      state.instance.plan.id = value.result?.plan.id
      state.instance.highAvailability = value.result?.highAvailability
    },
    SETTING_INSTANCE_STATUS(state, value) {
      state.settingInstanceStatus = value
    },
    ENABLE_FIREWALL(state, val) {
      state.instance.isFirewallEnabled = val
    },
    SET_FIREWALL(state, id) {
      state.instance.firewallId = id
    },
    SET_CURRENT_PAGE(state, value) {
      state.currentPage = value
    },
    // DATACENTERS
    SET_DATACENTERS(state, value) {
      state.datacenters = value.result
    },
    SET_FETCHING_DATACENTERS(state, value) {
      state.fetchingDatacenters = value
    },
    // PLANS
    SET_PLANS(state, value) {
      state.plans = value.result
    },
    SET_FETCHING_PLANS(state, value) {
      state.fetchingPlans = value
    },
    // IMAGES
    SET_IMAGES(state, value) {
      state.images = value.result
    },
    SET_FETCHING_IMAGES(state, value) {
      state.fetchingImages = value
    },
    // CONSOLE
    SET_CONSOLE(state, value) {
      state.console = value.result
    },
    SET_GETTING_CONSOLE(state, value) {
      state.gettingConsole = value
    },
  },
  actions: {
    fetchInstances({ commit }, payload) {
      const ext = payload
        ? `?PageIndex=${payload.pageIndex}&PageSize=${payload.pageSize}`
        : ''
      commit('SET_FETCHING_INSTANCES', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .get(`/projects/${getProjectId()}/instances${ext}`)
          .then(res => {
            commit('SET_INSTANCES', res.data)
            resolve(res)
          })
          .catch(err => {
            if (err.response?.status === 426 || err.response?.status === 428) {
              commit('SET_SERVICE_UNAVAILABLE', err.response?.status === 428)
            }
            reject(err)
          })
          .finally(() => {
            commit('SET_FETCHING_INSTANCES', false)
          })
      })
    },
    getInstance({ commit }, id) {
      commit('SET_GETTING_INSTANCE', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .get(`/projects/${getProjectId()}/instances/${id}`)
          .then(res => {
            commit('SET_INSTANCE', res.data)
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_GETTING_INSTANCE', false)
          })
      })
    },
    addInstance({ commit }, instance) {
      commit('SET_ADDING_INSTANCE', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .post(`/projects/${getProjectId()}/instances`, instance)
          .then(res => {
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_ADDING_INSTANCE', false)
          })
      })
    },
    updateInstance({ commit }, instance) {
      commit('SET_UPDATING_INSTANCE', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .put(`/projects/${getProjectId()}/instances/${instance.id}`, instance)
          .then(res => {
            commit('SET_INSTANCE_PLAN', res.data)
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_UPDATING_INSTANCE', false)
          })
      })
    },
    removeInstance({ commit }, id) {
      commit('SET_REMOVING_INSTANCE', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .delete(`/projects/${getProjectId()}/instances/${id}`)
          .then(res => {
            // commit('REMOVE_INSTANCE', id)
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_REMOVING_INSTANCE', false)
          })
      })
    },
    enableFirewall({ commit }, query) {
      commit('SET_ENABLING_FIREWALL', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .put(
            `/projects/${getProjectId()}/instances/${query.id}/enable-firewall`,
            query.firewall,
          )
          .then(res => {
            commit('ENABLE_FIREWALL', query.firewall.isActive)
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_ENABLING_FIREWALL', false)
          })
      })
    },
    setFirewall({ commit }, query) {
      commit('SETTING_FIREWALL', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .put(
            `/projects/${getProjectId()}/instances/${query.id}/set-firewall`,
            query.firewall,
          )
          .then(res => {
            commit('SET_FIREWALL', query.firewall.firewallId)
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SETTING_FIREWALL', false)
          })
      })
    },
    setInstanceStatus({ commit }, payload) {
      commit('SETTING_INSTANCE_STATUS', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .put(
            `/projects/${getProjectId()}/instances/${payload.id}/status`,
            payload.instance,
          )
          .then(res => {
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SETTING_INSTANCE_STATUS', false)
          })
      })
    },
    // eslint-disable-next-line no-empty-pattern
    validateInstanceName({}, name) {
      return new Promise(resolve => {
        api('compute')
          .head(`/projects/${getProjectId()}/instances/${name}`)
          .then(() => {
            resolve({ isValid: false, nameExists: true })
          })
          .catch(err => {
            if (err.response && err.response.status === 404) resolve({ isValid: true, nameExists: false })
            resolve({ isValid: false, nameExists: false })
          })
      })
    },
    // DATA CENTERS
    fetchDatacenters({ commit }) {
      commit('SET_FETCHING_DATACENTERS', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .get(`/projects/${getProjectId()}/datacenters`)
          .then(res => {
            commit('SET_DATACENTERS', res.data)
            resolve(res)
          })
          .catch(err => {
            if (err.response?.status === 426 || err.response?.status === 428) {
              commit('SET_SERVICE_UNAVAILABLE', err.response?.status === 428)
            }
            reject(err)
          })
          .finally(() => {
            commit('SET_FETCHING_DATACENTERS', false)
          })
      })
    },
    // PLANS
    fetchPlans({ commit }, id) {
      commit('SET_FETCHING_PLANS', true)
      const planId = id ? `?currentPlanId=${id}` : ''
      return new Promise((resolve, reject) => {
        api('compute')
          .get(`/projects/${getProjectId()}/plans${planId}`)
          .then(res => {
            commit('SET_PLANS', res.data)
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_FETCHING_PLANS', false)
          })
      })
    },
    // IMAGES
    fetchImages({ commit }) {
      commit('SET_FETCHING_IMAGES', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .get(`/projects/${getProjectId()}/images`)
          .then(res => {
            commit('SET_IMAGES', res.data)
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_FETCHING_IMAGES', false)
          })
      })
    },
    // CONSOLE
    getConsole({ commit }, id) {
      commit('SET_GETTING_CONSOLE', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .get(`/projects/${getProjectId()}/instances/${id}/console`)
          .then(res => {
            commit('SET_CONSOLE', res.data)
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_GETTING_CONSOLE', false)
          })
      })
    },
  },
}
